/**
 * 訂單列表的支付篩選選項與 query string 組裝；UI 在 components/OrderFilters.vue。
 *
 * option 的 value 一律是 DB 原始值，不是畫面顯示值：
 *  - 信用卡的 payment_type 存 'credit'，列表才改寫成 'direct' 顯示。
 *  - cardpay 涵蓋 CPG 與富邦，只能靠 payment_services.is_cpg 分辨；
 *    /api/ddpay-orders 跨 DB 無法篩，故該頁只有合併的 cardpay。
 *  - hasPayType 標記信用卡類服務，OrderFilters 只看這個旗標。
 *
 * 兩頁選項不同，因為 payment_service 為 NULL 的語意不同：金流頁是 legacy 舊單
 * （可由 payment_type 推導），DDPay 頁是未進 DDPG（只能用結帳模式篩）。
 */

// 各自成一個選項的錢包服務，label 取自 common.*。
const WALLET_SERVICES = [
  'linepay', 'jko', 'easywallet', 'icashpay',
  'pxpayplus', 'pluspay', 'ipassmoney', 'twqr', 'aftee', 'taiwanpay'
];

// fastek（GCash／Maya）與 olink 量少且無 icon，合併成一個「其他／海外」選項。
const OTHER_OVERSEAS_SERVICES = [ 'fastek', 'olink' ];

const DDPW_PAYMENT_MODES = [ 'sd', 'md', 'cpm', 'ba', 'ttp', 'cd' ];

const walletOptions = t => WALLET_SERVICES.map(service => ({
  value: service,
  label: t(`common.${service}`),
  payServices: [ service ]
}));

export const PaymentFiltersMixin = {
  computed: {
    // 用明確的「全部」選項而非 placeholder：placeholder 在 outlined + label 的
    // v-select 上要 Vuetify >= 2.6 的 persistent-placeholder 才顯示，而 yarn.lock
    // 仍鎖 2.4.3。空字串在 buildPaymentFilterQuery 的 truthy 判斷下天然等於不篩選。
    allOption() {
      return { value: '', label: this.$t('views.orders.allOption') };
    },

    otherOverseasOption() {
      return {
        value: 'other-overseas',
        label: this.$t('views.orders.serviceMap.otherOverseas'),
        payServices: OTHER_OVERSEAS_SERVICES
      };
    },

    /** /overview/orders：信用卡拆成三個收單通路，靠卡感應與藍新可獨立篩。 */
    paymentServiceOptions() {
      let t = key => this.$t(key);
      return [
        this.allOption,
        { value: 'cardpay-cpg', label: t('views.orders.serviceMap.cardpayCpg'), payServices: [ 'cardpay' ], isCpg: 1, hasPayType: true },
        { value: 'cardpay-legacy', label: t('views.orders.serviceMap.cardpayLegacy'), payServices: [ 'cardpay' ], isCpg: 0, hasPayType: true },
        { value: 'tappay', label: t('views.orders.serviceMap.cardpayTappay'), payServices: [ 'tappay' ], hasPayType: true },
        { value: 'ddtap', label: t('common.ddtap'), payServices: [ 'ddtap' ] },
        ...walletOptions(t),
        { value: 'newebpay', label: t('common.newebpay'), payServices: [ 'newebpay' ] },
        this.otherOverseasOption
      ];
    },

    /**
     * /overview/ddpay-orders：CPG 與富邦合併（該頁 DB 分不出來）；
     * 靠卡感應與藍新不列 —— ddpg_orders 沒有這兩個值，列了會篩出 0 筆。
     */
    ddpayPaymentServiceOptions() {
      let t = key => this.$t(key);
      return [
        this.allOption,
        { value: 'cardpay', label: t('common.cardpay'), payServices: [ 'cardpay' ], hasPayType: true },
        { value: 'tappay', label: t('views.orders.serviceMap.cardpayTappay'), payServices: [ 'tappay' ], hasPayType: true },
        ...walletOptions(t),
        this.otherOverseasOption
      ];
    },

    /** ddpay_web.orders.payment_mode，與 payment_service 正交。 */
    ddpwPaymentModeOptions() {
      return [ this.allOption, ...DDPW_PAYMENT_MODES.map(mode => ({
        value: mode,
        label: this.$t(`views.orders.paymentModeMap.${mode}`)
      })) ];
    },

    paymentTypeOptions() {
      return [
        this.allOption,
        { value: 'credit', label: this.$t('common.credit') },
        { value: 'apple', label: this.$t('common.apple') },
        { value: 'google', label: this.$t('common.google') }
      ];
    },

    /** qrpay.payment_orders.status */
    qrpayOrderStatusOptions() {
      return [ this.allOption, ...[ 'issued', 'completed', 'failed', 'refund' ].map(status => ({
        value: status,
        label: this.$t(`views.orders.statusMap.${status}`)
      })) ];
    },

    /** ddpay_web.orders.status，與 qrpay 不同套詞彙。 */
    ddpwOrderStatusOptions() {
      return [ this.allOption, ...[ 'issued', 'done', 'failed', 'cancelled' ].map(status => ({
        value: status,
        label: this.$t(`views.orders.statusMap.${status}`)
      })) ];
    }
  },

  methods: {
    /**
     * 各頁 loadUrl 已帶 '?'，故一律以 '&' 串接。
     * 必須保持 pure —— 它在 computed 鏈上，在此 throw 會被 Vue 的 _render()
     * 吞掉、讓頁面靜默停在舊畫面。選項與 v-model 不同步由 OrderFilters 的
     * watch 收斂（會清掉選取值，畫面上看得見）。
     */
    buildPaymentFilterQuery(options, { service, payType, status, mode }) {
      let query = '';
      let selected = service? options.find(o => o.value === service): null;

      if (selected) {
        query += `&payService=${encodeURIComponent(selected.payServices.join(','))}`;
        if (selected.isCpg !== undefined)
          query += `&isCpg=${selected.isCpg}`;
      }
      if (payType && selected && selected.hasPayType)
        query += `&payType=${encodeURIComponent(payType)}`;
      if (status)
        query += `&status=${encodeURIComponent(status)}`;
      if (mode)
        query += `&paymentMode=${encodeURIComponent(mode)}`;

      return query;
    },

    onLoadError(err) {
      this.$notify({ text: (err && err.message) || this.$t('views.orders.loadFailed'), type: 'error' });
    },

    /** 未知通路退回原始值，避免把 i18n key 路徑印到表格上。呼叫端自行擋 null。 */
    acquirerChannelLabel(channel) {
      let key = `views.orders.acquirerMap.${channel}`;
      return this.$te(key)? this.$t(key): channel;
    },

    acquirerChannelColor(channel) {
      return channel === 'cpg'? 'primary': 'grey lighten-1';
    },

    /** 結帳模式的顯示文字，與下拉共用同一份 i18n。 */
    paymentModeLabel(mode) {
      let key = `views.orders.paymentModeMap.${mode}`;
      return this.$te(key)? this.$t(key): mode;
    }
  }
};
